import authorizedAPI from '../../api/authorizedAPI';
import {
  mapFormulaResponse,
  parseFormulaResponse,
} from './formulaMateResponse';
import type {
  FormulaMateRequest,
  FormulaMateResponse,
  FormulaMateService,
} from './formulaMate.types';
import type { FormulaMessageRequest } from './types/formulaApi.types';

export class FormulaMateIntegrationError extends Error {
  constructor() {
    super(
      'FormulaMate AI is waiting for its BFF integration. Your input has not been sent. Please try again once the service is configured.'
    );
    this.name = 'FormulaMateIntegrationError';
  }
}

export class FormulaMateEmptyResponseError extends Error {
  constructor() {
    super('FormulaMate AI returned an empty response. Please try again.');
    this.name = 'FormulaMateEmptyResponseError';
  }
}

export class FormulaMateRequestError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'FormulaMateRequestError';
  }
}

export interface FormulaMateContract {
  createSessionBody: () => unknown;
  readSessionId: (data: unknown) => string;
  messageBody: (request: FormulaMateRequest, sessionId: string) => unknown;
  readResponse: (data: unknown) => FormulaMateResponse;
}

export function readFormulaSessionId(data: unknown): string {
  if (
    typeof data !== 'object' ||
    data === null ||
    !('session_id' in data) ||
    typeof data.session_id !== 'string' ||
    !data.session_id.trim()
  ) {
    throw new Error('FormulaMate AI returned an invalid session.');
  }
  return data.session_id;
}

const bffContract: FormulaMateContract = {
  createSessionBody: () => undefined,
  readSessionId: readFormulaSessionId,
  messageBody: (request, sessionId): FormulaMessageRequest => ({
    session_id: sessionId,
    operation: request.workflow === 'review' ? 'REVIEW' : 'GENERATE',
    message: request.workflow === 'review' ? request.code : request.prompt,
  }),
  readResponse: data => mapFormulaResponse(parseFormulaResponse(data)),
};

export const createFormulaMateService = (
  contract: FormulaMateContract | undefined
): FormulaMateService => {
  const requireContract = () => {
    if (!contract) throw new FormulaMateIntegrationError();
    return contract;
  };

  const sendMessage = async (
    sessionId: string,
    request: FormulaMateRequest,
    signal: AbortSignal
  ): Promise<FormulaMateResponse> => {
    const mapping = requireContract();
    const { data } = await authorizedAPI.post<unknown>(
      `/chat/session/${encodeURIComponent(sessionId)}/message`,
      mapping.messageBody(request, sessionId),
      { signal, timeout: 60000 }
    );
    const response = mapping.readResponse(data);
    const hasContent = response.blocks.some(block => {
      switch (block.type) {
        case 'text':
          return !!block.text.trim();
        case 'code':
          return !!block.code.trim();
        case 'section':
          return block.items.some(item => !!item.trim());
      }
    });
    if (!hasContent) throw new FormulaMateEmptyResponseError();
    return response;
  };

  return {
    async createSession(signal) {
      const mapping = requireContract();
      const { data } = await authorizedAPI.post<unknown>(
        '/chat/session',
        mapping.createSessionBody(),
        { signal, timeout: 60000 }
      );
      const id = mapping.readSessionId(data);
      if (!id.trim()) {
        throw new Error('FormulaMate AI returned an invalid session.');
      }
      return id;
    },
    generateFormula: (sessionId, prompt, signal) =>
      sendMessage(sessionId, { workflow: 'generation', prompt }, signal),
    reviewCode: (sessionId, code, instructions, signal) =>
      sendMessage(
        sessionId,
        { workflow: 'review', code, instructions },
        signal
      ),
    async endSession(sessionId, signal) {
      await authorizedAPI.delete(
        `/chat/session/${encodeURIComponent(sessionId)}`,
        { signal, timeout: 60000 }
      );
    },
  };
};

export const formulaMateService = createFormulaMateService(bffContract);
